import React from 'react';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import Row from 'components/Row';
import Text from 'components/Text';
import Link from 'components/Link';
import { regex } from 'lib/assertions';
import Buttons from 'components/Buttons';
import RosteredDevice from '../RosteredDeviceModal/RosteredDevice';

const emailRegexp = new RegExp(regex.email);

export type RemoveContactInfoDialogProps = {
  deviceUid: string;
  name: string;
  isOnlyDevice: boolean;
  isRostered: boolean;
  loading?: boolean;
  onRequestHide: () => void;
  onRemoveRequest: () => void;
  onEditRequest: () => void;
};

const RemoveContactInfoDialog = ({
  deviceUid,
  name,
  isOnlyDevice,
  isRostered,
  loading,
  onRequestHide,
  onRemoveRequest,
  onEditRequest,
}: RemoveContactInfoDialogProps): JSX.Element => {
  const Content = (): JSX.Element => {
    if (isRostered) {
      return (<RosteredDevice name={name} deviceUid={deviceUid} onComplete={onRequestHide}/>);
    }

    if (isOnlyDevice) {
      return (
        <BoxBody>
          <Row size="medium">
            <Text size="xsmall" align="left">
              <Text weight="semibold">{deviceUid}</Text> is their only contact information, so it can only be edited, not removed.
            </Text>
          </Row>

          <Buttons fullButtonIndex={0} axis="y">
            <Button
              level="secondary"
              data-test="remove-button"
              eventElementName="RemoveContactInfoDialog-submit"
              onClick={() => {
                onRequestHide();
                onEditRequest();
              }}
              type="submit"
              color="mascot"
              loading={loading}
              full>
              Edit contact information
            </Button>
          
            <Link 
              data-test="cancel-button" 
              eventElementName="RemoveContactInfoDialog-cancel"
              onClick={onRequestHide}>
                Cancel
            </Link>
          </Buttons>
        </BoxBody>
      );
    }

    return (
      <BoxBody>
        <Row size="medium">
          <Text size="xsmall" align="left">
            Remove <Text weight="semibold">{deviceUid}</Text> from {name}? For security, they will
            be notified of the removal.
          </Text>
        </Row>

        <Button
          data-test="submit-button"
          eventElementName="RemoveContactInfoDialog-submit"
          onClick={onRemoveRequest}
          type="submit"
          level="primary"
          color="apple"
          loading={loading}
          full>
          Remove {emailRegexp.test(deviceUid) ? 'email' : 'phone number'}
        </Button>
      </BoxBody>
    );
  };

  return (
    <Dialog
      title={
        isOnlyDevice || isRostered ? 'Cannot remove contact information' : 'Remove contact information'
      }
      size="xsmall"
      onRequestHide={onRequestHide}>
      <Content />
    </Dialog>
  );
};

export default RemoveContactInfoDialog;
